import React, { useEffect } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Button,
  Grid,
  Fade,
  Zoom
} from '@mui/material';
import {
  Check,
  ArrowBack,
} from '@mui/icons-material';
import LoadingImage from '../components/LoadingImage';

const AracIciYalitim: React.FC = () => {
  // SEO Meta Tags
  useEffect(() => {
    document.title = "Araç İçi Frigo İzolasyon ve Yalıtım - Kale Treyler Frigo | Mersin";
    
    // Update meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', "Araç içi frigo izolasyon ve yalıtım hizmetleri. Poliüretan izolasyon, enerji verimliliği ve profesyonel yalıtım çözümleri. Mersin'de Kale Treyler Frigo.");
    }
    
    // Update meta keywords
    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', "araç içi yalıtım, frigo izolasyon, poliüretan izolasyon, enerji verimliliği, Mersin yalıtım");
    }
    
    // Update Open Graph tags
    const ogTitle = document.querySelector('meta[property="og:title"]');
    if (ogTitle) {
      ogTitle.setAttribute('content', "Araç İçi Frigo İzolasyon ve Yalıtım - Kale Treyler Frigo | Mersin");
    }
    
    const ogDescription = document.querySelector('meta[property="og:description"]');
    if (ogDescription) {
      ogDescription.setAttribute('content', "Araç içi frigo izolasyon ve yalıtım hizmetleri. Poliüretan izolasyon, enerji verimliliği ve profesyonel yalıtım çözümleri.");
    }

    // Update Canonical URL
    const canonicalLink = (document.querySelector('link[rel="canonical"]') || document.createElement('link')) as HTMLLinkElement;
    canonicalLink.setAttribute('rel', 'canonical');
    canonicalLink.setAttribute('href', 'https://kaletreyler.com/hizmetler/arac-ici-yalitim');
    if (!document.head.contains(canonicalLink)) {
      document.head.appendChild(canonicalLink);
    }
  }, []);

  const features = [
    {
      title: 'Poliüretan İzolasyon',
      description: 'En yüksek kalitede poliüretan izolasyon malzemeleri ile mükemmel ısı yalıtımı sağlıyoruz. Frigo dorse kasasının en kritik bölgesi olan köprü kısmında özel izolasyon çözümleri uyguluyoruz.',
      benefits: ['Yüksek ısı izolasyonu', 'Uzun ömürlü malzeme', 'Nem direnci', 'Kolay uygulama', 'Kritik bölge koruması', 'Sızdırmazlık', 'Dayanıklılık', 'Profesyonel uygulama'],
      image: '/25.png'
    },
    {
      title: 'Enerji Verimliliği',
      description: 'İzolasyon sayesinde soğutma sisteminizin enerji tüketimini %40-50 oranında azaltıyoruz. Uzman ekibimizle hızlı ve etkili izolasyon uygulaması yapıyoruz.',
      benefits: ['Düşük enerji tüketimi', 'Çevre dostu', 'Maliyet tasarrufu', 'Sürdürülebilirlik', 'Hızlı teslim', 'Minimum iş durması', 'Kaliteli işçilik', 'Garantili hizmet'],
      image: '/26.png'
    }
  ];

  const process = [
    {
      step: 1,
      title: 'İnceleme ve Analiz',
      description: 'Mevcut yalıtım durumunu detaylı olarak inceliyor ve analiz ediyoruz.'
    },
    {
      step: 2,
      title: 'Malzeme Seçimi',
      description: 'Araç tipinize ve kullanım alanınıza uygun en kaliteli yalıtım malzemesini seçiyoruz.'
    },
    {
      step: 3,
      title: 'Hazırlık ve Temizlik',
      description: 'Uygulama öncesi gerekli hazırlıkları yapıyor ve alanı temizliyoruz.'
    },
    {
      step: 4,
      title: 'Yalıtım Uygulaması',
      description: 'Uzman teknisyenlerimizle profesyonel yalıtım uygulaması gerçekleştiriyoruz.'
    },
    {
      step: 5,
      title: 'Kalite Kontrolü',
      description: 'Uygulanan yalıtımın kalitesini test ediyor ve gerekli düzeltmeleri yapıyoruz.'
    }
  ];

  return (
    <Box>
      {/* Hero Section */}
      <Box
        sx={{
          minHeight: '60vh',
          background: 'linear-gradient(135deg, #ffffff 0%, #f1f5f9 50%, #e2e8f0 100%)',
          display: 'flex',
          alignItems: 'center',
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'radial-gradient(circle at 20% 80%, rgba(30, 58, 138, 0.1) 0%, transparent 50%), radial-gradient(circle at 80% 20%, rgba(30, 58, 138, 0.1) 0%, transparent 50%)',
            zIndex: 0,
          }
        }}
      >
        <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 1, px: { xs: 2, md: 4, lg: 6 } }}>
          <Grid container spacing={{ xs: 4, md: 6, lg: 8 }} alignItems="center">
            <Grid item xs={12} lg={6}>
              <Fade in timeout={1000}>
                <Box>
                  <Button
                    component={RouterLink}
                    to="/hizmetler"
                    startIcon={<ArrowBack />}
                    sx={{ mb: 3, textTransform: 'none' }}
                  >
                    Hizmetlere Dön
                  </Button>
                  <Typography variant="h1" component="h1" gutterBottom sx={{ 
                    fontWeight: 800, 
                    color: 'text.primary',
                    fontSize: { xs: '2.5rem', md: '3.5rem', lg: '4rem' },
                    lineHeight: 1.1,
                    mb: 3
                  }}>
                    Araç İçi Frigo İzolasyon ve Yalıtım
                  </Typography>
                  <Typography variant="h5" paragraph sx={{ 
                    mb: 4, 
                    color: 'text.secondary',
                    lineHeight: 1.6
                  }}>
                    Frigo dorse kasalarında profesyonel izolasyon ve yalıtım çözümleri ile maksimum enerji verimliliği sağlıyoruz
                  </Typography>
                  
                    
                 
                </Box>
              </Fade>
            </Grid>
            <Grid item xs={12} lg={6}>
              <Fade in timeout={1500}>
                <Box
                  sx={{
                    position: 'relative',
                    borderRadius: 3,
                    overflow: 'hidden',
                    boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
                    '&:hover': {
                      transform: 'scale(1.02)',
                      transition: 'transform 0.3s ease',
                    }
                  }}
                >
                  <LoadingImage
                    src="/22.png"
                    alt="Araç İçi Frigo İzolasyon ve Yalıtım Hizmeti"
                    style={{
                      width: '100%',
                      height: '400px',
                      objectFit: 'cover',
                    }}
                  />
                  <Box
                    sx={{
                      position: 'absolute',
                      bottom: 0,
                      left: 0,
                      right: 0,
                      background: 'linear-gradient(transparent, rgba(0,0,0,0.7))',
                      p: 3,
                      color: 'white',
                    }}
                  >
                    <Typography variant="h5" component="h3" sx={{ fontWeight: 'bold', mb: 1 }}>
                      Profesyonel Yalıtım
                    </Typography>
                    <Typography variant="body1" sx={{ opacity: 0.9 }}>
                      Enerji verimliliği ve tasarruf için uzman çözümler
                    </Typography>
                  </Box>
                </Box>
              </Fade>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Features Section */}
      <Container maxWidth="xl" sx={{ py: 8, px: { xs: 2, md: 4, lg: 6 } }}>
        <Box textAlign="center" mb={5}>
          <Typography variant="h3" component="h2" gutterBottom color="primary" sx={{ fontWeight: 'bold' }}>
            İzolasyon ve Yalıtım Hizmetlerimiz
          </Typography>
          <Typography variant="h6" color="text.secondary" sx={{ maxWidth: '600px', mx: 'auto' }}>
            Frigo dorse kasalarınızda en kaliteli izolasyon ve yalıtım çözümlerini sunuyoruz
          </Typography>
        </Box>

        <Grid container spacing={{ xs: 3, md: 4, lg: 5 }}>
          {features.map((feature, index) => (
            <Grid item xs={12} md={6} key={index}>
              <Zoom in timeout={1000 + index * 200}>
                <Card
                  elevation={0}
                  sx={{
                    height: '100%',
                    p: 2.5,
                    borderRadius: 3,
                    border: '1px solid',
                    borderColor: 'grey.200',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
                      borderColor: 'primary.main',
                    },
                  }}
                >
                  <CardContent>
                    {/* Feature Image */}
                    {feature.image && (
                      <Box sx={{ mb: 2, textAlign: 'center' }}>
                        <LoadingImage
                          src={feature.image}
                          alt={feature.title}
                          style={{
                            width: '200px',
                            height: '150px',
                            objectFit: 'cover',
                            borderRadius: '12px',
                          }}
                        />
                      </Box>
                    )}
                    
                    <Typography variant="h5" component="h3" gutterBottom sx={{ fontWeight: 'bold', mb: 2, textAlign: 'center' }}>
                      {feature.title}
                    </Typography>
                    <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.5, mb: 1.5, textAlign: 'center' }}>
                      {feature.description}
                    </Typography>
                    <Grid container spacing={1}>
                      {feature.benefits.map((benefit, benefitIndex) => (
                        <Grid item xs={6} key={benefitIndex}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5 }}>
                            <Check color="primary" sx={{ fontSize: 16 }} />
                            <Typography variant="body2" color="text.secondary">
                              {benefit}
                            </Typography>
                          </Box>
                        </Grid>
                      ))}
                    </Grid>
                  </CardContent>
                </Card>
              </Zoom>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Process Section */}
      <Box sx={{ bgcolor: 'grey.50', py: 8 }}>
        <Container maxWidth="xl" sx={{ px: { xs: 2, md: 4, lg: 6 } }}>
          <Box textAlign="center" mb={5}>
            <Typography variant="h3" component="h2" gutterBottom color="primary" sx={{ fontWeight: 'bold' }}>
              İzolasyon ve Yalıtım Sürecimiz
            </Typography>
            <Typography variant="h6" color="text.secondary" sx={{ maxWidth: '600px', mx: 'auto' }}>
              Profesyonel izolasyon ve yalıtım hizmetimizi nasıl gerçekleştirdiğimizi keşfedin
            </Typography>
          </Box>

          <Grid container spacing={4}>
            {process.map((step, index) => (
              <Grid item xs={12} md={6} lg={4} key={index}>
                <Fade in timeout={1000 + index * 200}>
                  <Card
                    elevation={0}
                    sx={{
                      height: '100%',
                      p: 3,
                      borderRadius: 3,
                      border: '1px solid',
                      borderColor: 'grey.200',
                      textAlign: 'center',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-5px)',
                        boxShadow: '0 10px 20px rgba(0,0,0,0.1)',
                      },
                    }}
                  >
                    <CardContent>
                      <Box
                        sx={{
                          width: 60,
                          height: 60,
                          borderRadius: '50%',
                          bgcolor: 'primary.main',
                          color: 'white',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          mx: 'auto',
                          mb: 3,
                          fontSize: '1.5rem',
                          fontWeight: 'bold'
                        }}
                      >
                        {step.step}
                      </Box>
                      <Typography variant="h6" component="h3" gutterBottom sx={{ fontWeight: 'bold' }}>
                        {step.title}
                      </Typography>
                      <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                        {step.description}
                      </Typography>
                    </CardContent>
                  </Card>
                </Fade>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>
    </Box>
  );
};

export default AracIciYalitim;
